import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articleSlugs, articles } from '@/mocks/articles';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies / Sweaters': 'ri-t-shirt-2-line',
  'T-Shirts': 'ri-t-shirt-line',
  'Jackets': 'ri-shirt-line',
  'Pants / Shorts': 'ri-pantone-line',
  'Headwear': 'ri-base-station-line',
  'Sets': 'ri-stack-line',
  'Underwear / Underpants': 'ri-box-3-line',
  'Jersey': 'ri-shirt-line',
  'Accessories': 'ri-glasses-line',
  'Bags': 'ri-briefcase-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeAll = () => {
    setCatDropdownOpen(false);
    setGuidesDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-6 md:px-10 py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="font-heading text-xl md:text-2xl text-foreground-900">
            Superbuy <span className="text-primary-500">Spreadsheet</span>
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
            Home
          </Link>

          {/* Categories Dropdown */}
          <div ref={catRef} className="relative">
            <button
              onClick={() => { setCatDropdownOpen(!catDropdownOpen); setGuidesDropdownOpen(false); }}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${catDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {catDropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 rounded-2xl shadow-lg border border-background-200/70 overflow-hidden z-50">
                <div className="max-h-96 overflow-y-auto py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      onClick={() => setCatDropdownOpen(false)}
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className={`${categoryIcons[cat.name] || 'ri-folder-line'} text-accent-600 text-xs`}></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Guides Dropdown */}
          <div ref={guidesRef} className="relative">
            <button
              onClick={() => { setGuidesDropdownOpen(!guidesDropdownOpen); setCatDropdownOpen(false); }}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Guides
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guidesDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {guidesDropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 bg-background-50 rounded-2xl shadow-lg border border-background-200/70 overflow-hidden z-50">
                <div className="max-h-96 overflow-y-auto py-3">
                  <p className="px-5 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">SEO Guides</p>
                  {articleSlugs.map((slug) => (
                    <Link
                      key={slug}
                      to={`/blog/${slug}`}
                      className="flex items-start gap-3 px-5 py-2.5 hover:bg-primary-50 transition-colors cursor-pointer"
                      onClick={() => setGuidesDropdownOpen(false)}
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0 mt-0.5">
                        <i className="ri-article-line text-accent-600 text-xs"></i>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-700 line-clamp-1">{articles[slug].h1}</p>
                        <p className="text-xs text-foreground-400">{articles[slug].readingTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop CTA */}
        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line"></i>
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Hamburger Menu */}
      {mobileMenuOpen && (
        <div ref={mobileRef} className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[70vh] overflow-y-auto">
          <div className="px-5 py-4">
            {/* Home Link */}
            <Link
              to="/"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 cursor-pointer"
              onClick={closeAll}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-home-line text-primary-600 text-sm"></i>
              </span>
              Home
            </Link>

            {/* Divider */}
            <div className="my-3 border-t border-background-200/70"></div>

            {/* Categories Section */}
            <p className="flex items-center gap-2 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              <i className="ri-grid-line"></i>
              Product Categories
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 py-2.5 px-2 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
                  onClick={closeAll}
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 flex-shrink-0">
                    <i className={`${categoryIcons[cat.name] || 'ri-folder-line'} text-accent-600 text-[10px]`}></i>
                  </span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            {/* Divider */}
            <div className="my-3 border-t border-background-200/70"></div>

            {/* Guides Section */}
            <p className="flex items-center gap-2 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              <i className="ri-book-read-line"></i>
              SEO Guides & Resources
            </p>
            <div className="space-y-1">
              {articleSlugs.slice(0, 6).map((slug) => (
                <Link
                  key={slug}
                  to={`/blog/${slug}`}
                  className="flex items-center gap-3 py-2.5 px-2 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
                  onClick={closeAll}
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-secondary-100 flex-shrink-0">
                    <i className="ri-article-line text-secondary-600 text-[10px]"></i>
                  </span>
                  <span className="truncate">{articles[slug].h1}</span>
                </Link>
              ))}
              {articleSlugs.length > 6 && (
                <p className="text-xs text-foreground-400 pl-9 py-1">
                  +{articleSlugs.length - 6} more guides available
                </p>
              )}
            </div>

            {/* Divider */}
            <div className="my-3 border-t border-background-200/70"></div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all cursor-pointer"
              onClick={closeAll}
            >
              <i className="ri-shopping-bag-line"></i>
              Buy It Now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}